import AsyncStorage from '@react-native-async-storage/async-storage';
import api from './apiClient';
import { getCloudFrontUrl, getS3Url } from './config';
import type { UploadResult, UploadFolder } from './storage';

const CHUNK_SIZE = 5 * 1024 * 1024; // 5 MB — S3 multipart minimum
const MAX_RETRIES = 3;
const RESUME_KEY_PREFIX = 'chunked_upload_';

export interface ChunkedUploadProgress {
  loaded: number;
  total: number;
  currentPart: number;
  totalParts: number;
  stage: string;
}

interface CompletedPart {
  partNumber: number;
  etag: string;
}

interface ResumeState {
  uploadId: string;
  key: string;
  totalSize: number;
  completedParts: CompletedPart[];
}

interface MultipartInitResponse {
  uploadId: string;
  key: string;
}

interface MultipartPresignResponse {
  presignedUrl: string;
}

export interface ChunkedUploadController {
  promise: Promise<UploadResult>;
  abort: () => void;
}

type ProgressCallback = (progress: ChunkedUploadProgress) => void;

function resumeStorageKey(uri: string, folder: string): string {
  let hash = 0;
  const str = `${uri}:${folder}`;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash + str.charCodeAt(i)) | 0;
  }
  return `${RESUME_KEY_PREFIX}${Math.abs(hash).toString(36)}`;
}

async function loadResumeState(key: string): Promise<ResumeState | null> {
  try {
    const raw = await AsyncStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

async function saveResumeState(key: string, state: ResumeState): Promise<void> {
  try {
    await AsyncStorage.setItem(key, JSON.stringify(state));
  } catch {
    // Best-effort persistence
  }
}

async function clearResumeState(key: string): Promise<void> {
  try {
    await AsyncStorage.removeItem(key);
  } catch {
    // Best-effort
  }
}

async function uploadPartWithRetry(
  presignedUrl: string,
  chunk: Blob,
): Promise<string> {
  let lastError: Error | null = null;

  for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
    try {
      const response = await fetch(presignedUrl, {
        method: 'PUT',
        body: chunk,
      });

      if (!response.ok) {
        throw new Error(`Part upload failed: HTTP ${response.status}`);
      }

      const etag = response.headers.get('ETag');
      if (!etag) throw new Error('Missing ETag in response');
      return etag;
    } catch (err) {
      lastError = err instanceof Error ? err : new Error(String(err));
      if (attempt < MAX_RETRIES - 1) {
        const delay = Math.pow(2, attempt) * 1000;
        await new Promise(r => setTimeout(r, delay));
      }
    }
  }

  throw lastError ?? new Error('Part upload failed after retries');
}

/**
 * Upload a video file using S3 multipart upload with chunking, retry, and
 * resume support. Returns a controller with a promise and abort handle.
 */
export function chunkedUploadVideo(
  uri: string,
  contentType: string,
  folder: UploadFolder,
  options?: { seriesId?: string; episodeId?: string; sourceBlob?: Blob },
  onProgress?: ProgressCallback,
): ChunkedUploadController {
  let aborted = false;
  let currentUploadId: string | null = null;
  let currentKey: string | null = null;

  const abort = () => {
    aborted = true;
    if (currentUploadId && currentKey) {
      api.post('/storage/multipart/abort', {
        key: currentKey,
        uploadId: currentUploadId,
      }).catch(() => {});
      const storeKey = resumeStorageKey(uri, folder);
      clearResumeState(storeKey);
    }
  };

  const promise = (async (): Promise<UploadResult> => {
    const storeKey = resumeStorageKey(uri, folder);

    let blob: Blob;
    if (options?.sourceBlob) {
      blob = options.sourceBlob;
    } else {
      const fileResponse = await fetch(uri);
      blob = await fileResponse.blob();
    }
    const totalSize = blob.size;
    const totalParts = Math.ceil(totalSize / CHUNK_SIZE);

    // Try to resume a previous upload
    let uploadId: string;
    let key: string;
    let completedParts: CompletedPart[] = [];

    const saved = await loadResumeState(storeKey);
    if (saved && saved.totalSize === totalSize) {
      uploadId = saved.uploadId;
      key = saved.key;
      completedParts = saved.completedParts;
    } else {
      if (saved) await clearResumeState(storeKey);

      const initResult = await api.post<MultipartInitResponse>(
        '/storage/multipart/initiate',
        {
          contentType,
          folder,
          ...(options?.seriesId && { seriesId: options.seriesId }),
          ...(options?.episodeId && { episodeId: options.episodeId }),
        },
      );

      if (initResult.error || !initResult.data) {
        throw new Error(initResult.error || 'Failed to initiate multipart upload');
      }

      uploadId = initResult.data.uploadId;
      key = initResult.data.key;
    }

    currentUploadId = uploadId;
    currentKey = key;

    await saveResumeState(storeKey, { uploadId, key, totalSize, completedParts });

    const completedPartNumbers = new Set(completedParts.map(p => p.partNumber));
    let loaded = completedParts.length * CHUNK_SIZE;
    if (loaded > totalSize) loaded = totalSize;

    for (let partNumber = 1; partNumber <= totalParts; partNumber++) {
      if (aborted) throw new Error('Upload aborted');

      if (completedPartNumbers.has(partNumber)) {
        onProgress?.({
          loaded: Math.min(partNumber * CHUNK_SIZE, totalSize),
          total: totalSize,
          currentPart: partNumber,
          totalParts,
          stage: `Part ${partNumber}/${totalParts} (cached)`,
        });
        continue;
      }

      onProgress?.({
        loaded,
        total: totalSize,
        currentPart: partNumber,
        totalParts,
        stage: `Uploading part ${partNumber} of ${totalParts}…`,
      });

      // Get presigned URL for this part
      const presignResult = await api.post<MultipartPresignResponse>(
        '/storage/multipart/presign-part',
        { key, uploadId, partNumber },
      );

      if (presignResult.error || !presignResult.data) {
        throw new Error(presignResult.error || 'Failed to get part presigned URL');
      }

      const start = (partNumber - 1) * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, totalSize);
      const chunk = blob.slice(start, end);

      const etag = await uploadPartWithRetry(presignResult.data.presignedUrl, chunk);

      completedParts.push({ partNumber, etag });
      loaded = end;

      await saveResumeState(storeKey, { uploadId, key, totalSize, completedParts });

      onProgress?.({
        loaded,
        total: totalSize,
        currentPart: partNumber,
        totalParts,
        stage: `Part ${partNumber}/${totalParts} done`,
      });
    }

    if (aborted) throw new Error('Upload aborted');

    // Complete the multipart upload
    onProgress?.({
      loaded: totalSize,
      total: totalSize,
      currentPart: totalParts,
      totalParts,
      stage: 'Finalizing…',
    });

    const completeResult = await api.post<{ key: string }>(
      '/storage/multipart/complete',
      {
        key,
        uploadId,
        parts: completedParts.map(p => ({
          partNumber: p.partNumber,
          etag: p.etag,
        })),
      },
    );

    if (completeResult.error || !completeResult.data) {
      throw new Error(completeResult.error || 'Failed to complete multipart upload');
    }

    await clearResumeState(storeKey);

    const url = getCloudFrontUrl(key) || getS3Url(key);
    return { key, url, size: totalSize };
  })();

  return { promise, abort };
}
